import React from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"
import { useTranslation } from "react-i18next"

import * as BikeApi from "features/bike/api/BikeApi"
import { useAlert, useAuth, useModal, useBikeStoreProcess } from "hooks"

import FormElement from "components/Form/FormElement"

import { normalizeBoolean } from "utils/functions"
import { isNotEmpty } from "utils"
import dateService from "services/date"
import {
	BIKE_BACK_STORE,
	OCODE_GENDERS_LIST,
	bikeTypes,
	bikeBrands,
	getBrandLabelFromValue,
	getLabelFromBikeId,
	REGISTER_MODE,
	UPDATE_MODE,
	BIKE_NOT_AVAILABLE,
} from "utils/constants"

import { OwnerDescription, BikeDescription } from "./Partials"
import { ErrorResponse, LocalBike } from "models"
import { FormError } from "models/form"

const defaultFormEvents = {
	ready: true,
}

type FormBikeInfosProps = {
	readonly: boolean
	saveMode?: string
	disableBikeInfos?: boolean
	toggleReadOnly?: () => void
}

export const FormBikeInfos: React.FC<FormBikeInfosProps> = ({
	readonly = false,
	saveMode = REGISTER_MODE,
	disableBikeInfos = false,
	toggleReadOnly = null,
}) => {
	const {
		setShowOwner: updateShowOwner,
		setCurrentBike,
		bikeProcessState,
	} = useBikeStoreProcess()
	const { currentBike: bike, bikeStatus } = bikeProcessState

	const [formFields, setFormFields] = React.useState(bike)
	const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)

	const { alert } = useAlert()
	const { showModal } = useModal()
	const { t } = useTranslation()
	const { entity, logout, isAdminOCode, isAdminBicycode } = useAuth()

	const isOCodeEntity = OCODE_GENDERS_LIST.includes(entity?.gender)
	const isViewedFromOCodeAdmin = isAdminOCode && !isOCodeEntity
	const isAdminUser = isAdminOCode || isAdminBicycode // user that can modify bike description
	const isRegisterMode = saveMode === REGISTER_MODE
	const isUpdateMode = saveMode === UPDATE_MODE
	const isBikeNotAvailable = !bikeStatus || bikeStatus === BIKE_NOT_AVAILABLE

	const bikeIsDisabled = (bike.status === BIKE_BACK_STORE && !isAdminOCode) || disableBikeInfos

	// Bike has already a description
	const isBikePreRegistered =
		!bikeStatus &&
		(bike.description_made_by === "STORE" || bike.description_made_by === "MANUFACTURER")

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target
		const value = target.type === "checkbox" ? target.checked : target.value
		const name = target.name

		setFormFields((s) => ({
			...s,
			[name]: value,
		}))
	}

	const handleValidation = React.useCallback((name: string, errs: Array<FormError>) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
	}, [])

	const prepareBikesDatas = (bike: LocalBike) => {
		// Prepare datas to send to API
		const descriptionData = {
			gear_type_id: bike.gear_type_id, // @TODO
			gear_type: bike.gear_type,
			brand_id: bike.brand_id,
			brand: bike.brand,
			model: bike.model,
			color: bike.color,
			is_electric: normalizeBoolean(bike.isVAE as string),
			...(isNotEmpty(bike.frame_serial_number)
				? { frame_serial_number: bike.frame_serial_number }
				: {}),
			...(bike.isVAE === "true" && isNotEmpty(bike.engine_serial_number)
				? { engine_serial_number: bike.engine_serial_number }
				: {}),
			...(bike.isVAE === "true" && isNotEmpty(bike.battery_serial_number)
				? { battery_serial_number: bike.battery_serial_number }
				: {}),
		}

		return {
			bike_id: bike.code,
			description_data: descriptionData,
			row_version: dateService.getCurrentDate("yyyyMMddHHmmssSSS"),
		}
	}

	const prepareBikeDescription = (bike: LocalBike) => {
		// Prepare datas to send to API
		const bikeDescription = {
			gear_type_id: bike.gear_type_id, // @TODO
			gear_type: bike.gear_type,
			brand_id: bike.brand_id,
			brand: bike.brand,
			model: bike.model,
			color: bike.color,
			is_electric: normalizeBoolean(bike.isVAE as string),
			...(isNotEmpty(bike.frame_serial_number)
				? { frame_serial_number: bike.frame_serial_number }
				: {}),
			...(bike.isVAE === "true" && isNotEmpty(bike.engine_serial_number)
				? { engine_serial_number: bike.engine_serial_number }
				: {}),
			...(bike.isVAE === "true" && isNotEmpty(bike.battery_serial_number)
				? { battery_serial_number: bike.battery_serial_number }
				: {}),
		}

		return {
			bike_id: bike.code,
			bike_description: bikeDescription,
			row_version: bike.row_version,
		}
	}

	const handlePreRegistration = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormEvents({
					...formEvents,
					ready: false,
				})

				const newBike = prepareBikesDatas(formFields)

				const { getError } = await BikeApi.editBikeDescription(newBike)
				if (getError()) throw getError()
				alert(
					"success",
					t("pre_registration.create_success", { ns: "bike", bikeId: newBike?.bike_id })
				)
				setFormEvents({
					...formEvents,
					ready: true,
				})
			} catch (error) {
				const erroMsg = (error as ErrorResponse)?.message?.toString()
				if (erroMsg) alert("error", erroMsg)

				if (["Access token is required."].includes((error as ErrorResponse)?.message))
					logout()
			}
		}
	}

	const handleSubmit = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			// hide error
			setShowLocalErrors(false)
			// prevent form to be submitted again
			setFormEvents({
				...formEvents,
				ready: false,
			})

			showModal(
				ModalConfirmation,
				{
					bike: formFields,
					updateShowOwner,
					callbackAndSell: () => {
						// We update the current bike in the context with formfields
						const newCurrentBike = {
							...formFields,
							gear_type:
								formFields.gear_type_id === 999
									? formFields.gear_type
									: t(`${getLabelFromBikeId(formFields.gear_type_id)}`),
						}

						setCurrentBike(newCurrentBike)
					},
				}
				// { style: { maxWidth: "850px" } }
			)
			setFormEvents({
				...formEvents,
				ready: true,
			})
		}
	}

	const handleUpdate = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)
		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			// hide error
			setShowLocalErrors(false)
			// prevent form to be submitted again
			setFormEvents({
				...formEvents,
				ready: false,
			})

			try {
				const updatedBike = prepareBikeDescription(formFields)
				const { getError, res } = await BikeApi.updateBikeDescription(updatedBike)
				if (getError()) throw getError()
				// Update BikeStore

				const newCurrentBike = {
					...formFields,
					row_version: res.row_version,
				}

				setCurrentBike(newCurrentBike)

				// Update formFields
				setFormFields((s) => {
					const updated = s
					updated.row_version = res.row_version
					if (s.isVAE === "false") {
						updated.engine_serial_number = ""
						updated.battery_serial_number = ""
					}
					return updated
				})
				alert("success", t("edit_success", { ns: "bike" }))
				toggleReadOnly() // close form
			} catch (error) {
				const erroMsg = (error as ErrorResponse)?.message?.toString()
				if (erroMsg) alert("error", erroMsg)
			}

			setFormEvents({
				...formEvents,
				ready: true,
			})
		}
	}

	/** Update form fields when bike is updated */
	React.useEffect(() => {
		setFormFields(bike)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bike])

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
	}

	return (
		<>
			{readonly && (
				<div className="flex">
					<BikeDescription
						className="flex-1"
						toggleReadOnly={!isBikeNotAvailable && isAdminUser ? toggleReadOnly : null}
					/>
					{isAdminOCode && !isViewedFromOCodeAdmin && (
						<OwnerDescription className="flex-1 pl-7" />
					)}
				</div>
			)}

			{!readonly && (
				<form data-cy="form-bike-infos" className="c-card p-5 md:p-12 mb-5">
					<section className="mb-5">
						<h2 className="c-h2 font-semibold mb-5">
							{t("description_title", { ns: "bike" })}
						</h2>
						<div className="l-grid u-mg-negative-hz-m">
							<div className="lg:w-1/2 w-full p-5">
								{/* brand */}
								<FormElement
									value={formFields.brand_id}
									defaultValue={{
										label: formFields.brand_id
											? t(`${getBrandLabelFromValue(formFields.brand_id)}`)
											: "",
										value: formFields.brand_id,
									}}
									placeholder="test"
									options={bikeBrands}
									name="brand_id"
									id="brand_id"
									type="select"
									label={t("brand", { ns: "bike" })}
									className={clsx("lg:pr-12 mb-5", {
										"min-w-300": !isMobileOnly,
									})}
									required={true}
									showErrors={showLocalErrors}
									onValidate={handleValidation}
									onChange={(event) => {
										const value = event.target.value
										const label = event.target.label
										setFormFields({
											...formFields,
											brand_id: value,
											brand: value === 999 ? "" : label,
										})
									}}
									disabled={bikeIsDisabled}
								/>

								{/* 999 = other brand */}
								{Number(formFields.brand_id) === 999 && (
									<FormElement
										value={formFields.brand}
										name="brand"
										type="text"
										label={t("brand_new", { ns: "bike" })}
										className={clsx("lg:pr-12 mt-7 mb-5", {
											"min-w-300": !isMobileOnly,
										})}
										required={true}
										showErrors={showLocalErrors}
										onValidate={handleValidation}
										onChange={handleInputChange}
										disabled={bikeIsDisabled}
									/>
								)}
							</div>

							<div className="lg:w-1/2 w-full p-5">
								{/* model */}
								<FormElement
									value={formFields.model}
									name="model"
									type="text"
									label={t("model", { ns: "bike" })}
									className="mb-5"
									required={true}
									showErrors={showLocalErrors}
									onValidate={handleValidation}
									onChange={handleInputChange}
									disabled={bikeIsDisabled}
								/>
							</div>

							<div className="lg:w-1/2 w-full p-5">
								{/* gear_type */}
								<FormElement
									value={formFields.gear_type_id}
									defaultValue={{
										label: formFields.gear_type_id
											? t(`${getLabelFromBikeId(formFields.gear_type_id)}`)
											: "",
										value: formFields.gear_type_id,
									}}
									options={bikeTypes.map((type) => ({
										...type,
										label: t(type.label),
									}))}
									name="gear_type_id"
									id="gear_type_id"
									type="select"
									label={t("type", { ns: "bike" })}
									className={clsx("lg:pr-12 mb-5", {
										"min-h-300": !isMobileOnly,
									})}
									required={true}
									showErrors={showLocalErrors}
									onValidate={handleValidation}
									onChange={(event) => {
										const value = event.target.value
										const label = t(event.target.label)
										setFormFields({
											...formFields,
											gear_type_id: value,
											gear_type: value === 999 ? "" : label,
										})
									}}
									disabled={bikeIsDisabled}
								/>

								{/* 999 = other type */}
								{Number(formFields.gear_type_id) === 999 && (
									<FormElement
										{...formElementCommonProps}
										value={formFields.gear_type}
										name="gear_type"
										type="text"
										label={t("type_new", { ns: "bike" })}
										className="lg:pr-12 mt-7 mb-5"
										style={{ minWidth: "300px" }}
										required={true}
										disabled={bikeIsDisabled}
									/>
								)}
							</div>

							<div className="lg:w-1/2 w-full p-5">
								{/* color */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.color}
									name="color"
									type="text"
									label={t("color", { ns: "bike" })}
									className="mb-5"
									required={true}
									disabled={bikeIsDisabled}
								/>
							</div>

							<div className="l-col-8 p-5">
								{/* VAE */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.isVAE}
									name="isVAE"
									type="radio"
									label={t("label_vae", { ns: "bike" })}
									// options={genderList.slice(0,4)}
									options={[
										{
											id: "vae-true",
											value: "true",
											label: "bike:label_vae_true",
										},
										{
											id: "vae-false",
											value: "false",
											label: "bike:label_vae_false",
										},
									]}
									className="mb-7"
									classNameOptionsContainer={clsx("flex-row w-full", {
										"flex-col": isMobileOnly,
									})}
									classNameLabel="mb-5"
									classNameOption="flex-1 items-center"
									required={true}
									disabled={bikeIsDisabled}
								/>
							</div>
						</div>
					</section>

					<section className="mb-5">
						<h2 className="c-h2 font-semibold mb-5">
							{t("optional_infos_title", { ns: "bike" })}
						</h2>
						<div className="l-grid u-mg-negative-hz-m">
							<div className="lg:w-1/2 w-full p-5">
								{/* frame_serial_number */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.frame_serial_number}
									name="frame_serial_number"
									type="text"
									label={t("serial", { ns: "bike" })}
									required={false}
								/>
							</div>
						</div>
						{normalizeBoolean(formFields?.isVAE as string) && (
							<div className="l-grid u-mg-negative-hz-m">
								<div className="lg:w-1/2 w-full p-5">
									{/* Bike Engine Serial */}
									<FormElement
										{...formElementCommonProps}
										value={formFields.engine_serial_number}
										name="engine_serial_number"
										type="text"
										label={t("serial_engine", { ns: "bike" })}
										required={false}
									/>
								</div>
								<div className="lg:w-1/2 w-full p-5">
									{/* Bike Battery Serial */}
									<FormElement
										{...formElementCommonProps}
										value={formFields.battery_serial_number}
										name="battery_serial_number"
										type="text"
										label={t("serial_battery", { ns: "bike" })}
										required={false}
									/>
								</div>
							</div>
						)}
					</section>

					{
						// bike is null and manufacturer describe bike => example with MFC
						!bikeStatus && bike?.description_made_by === "MANUFACTURER" && (
							<p className="text-danger mt-12">
								{t("pre_registration.manufaturer_description_modification", {
									ns: "bike",
								})}
							</p>
						)
					}

					<div
						className={clsx(
							`flex ${
								isRegisterMode ? "justify-between" : "flex-row-reverse"
							} u-relative mt-12 mb-5`,
							{ "flex-col": isMobileOnly }
						)}
					>
						{!formEvents.ready && <div className="c-spinner"></div>}
						{formEvents.ready && (
							<>
								{bikeStatus && isRegisterMode && (
									<button
										data-cy="submit-register"
										type="submit"
										className="c-btn c-btn--primary px-5 uppercase ml-auto"
										onClick={handleSubmit}
									>
										{t("global.save")}
									</button>
								)}
								{bikeStatus && isAdminUser && isUpdateMode && (
									<>
										<button
											data-cy="submit-update"
											type="submit"
											className="c-btn c-btn--primary px-5 ml-2"
											onClick={handleUpdate}
										>
											{t("global.update")}
										</button>
										{toggleReadOnly && (
											<button
												data-cy="submit-toggleReadOnly"
												className="c-btn px-5"
												onClick={toggleReadOnly}
											>
												{t("global.cancel")}
											</button>
										)}
									</>
								)}
								{!bikeStatus && (
									<>
										<div className={clsx({ "mb-12": isMobileOnly })}>
											<p
												className={clsx(
													"uppercase",
													{
														"mb-5": !isMobileOnly,
													},
													{
														"mb-2 ": isMobileOnly,
													}
												)}
											>
												{bike?.description_made_by === "MANUFACTURER"
													? t("pre_registration.manufacturer_describe", {
															ns: "bike",
														})
													: bike?.description_made_by === "STORE"
														? t("pre_registration.store_describe", {
																ns: "bike",
															})
														: t(
																"pre_registration.register_description",
																{
																	ns: "bike",
																}
															)}
											</p>
											<button
												data-cy="submit-preregistration"
												type="submit"
												className={
													"c-btn px-5 uppercase " +
													(isBikePreRegistered
														? "c-btn--secondary"
														: "c-btn--primary")
												}
												onClick={handlePreRegistration}
											>
												{isBikePreRegistered
													? t("global.update")
													: t("pre_registration.pre_registration", {
															ns: "bike",
														})}
											</button>
										</div>
										<div>
											<p
												className={clsx(
													"uppercase",
													{
														"mb-5": !isMobileOnly,
													},
													{
														"mb-2 ": isMobileOnly,
													}
												)}
											>
												{t("pre_registration.add_owner", { ns: "bike" })}
											</p>
											<button
												data-cy="submit-global"
												type="submit"
												className={clsx(
													"c-btn c-btn--primary px-5 uppercase",
													{ "ml-auto": !isMobileOnly }
												)}
												onClick={handleSubmit}
											>
												{t("global.save")}
											</button>
										</div>
									</>
								)}
							</>
						)}
					</div>
				</form>
			)}
		</>
	)
}

const ModalConfirmation = ({ bike, callbackAndSell, updateShowOwner }) => {
	const { hideModal } = useModal()
	const { t } = useTranslation()

	const validateAndSell = (response) => {
		hideModal()
		callbackAndSell(response)
		if (updateShowOwner) updateShowOwner(true)
	}

	return (
		<div className="p-12">
			<h2 className="c-h2 text-primary text-xl font-bold text-center my-7">
				{t("create_modal_confirm_title", { ns: "bike" })}
			</h2>
			<p className="">
				{t("create_modal_confirm_description_1", { ns: "bike" })}{" "}
				<strong>{t("create_modal_confirm_description_2", { ns: "bike" })}</strong>
			</p>

			<p className="mt-7 mb-2">{t("create_modal_confirm_description_3", { ns: "bike" })}</p>
			<p className="font-bold text-m">
				<strong>{bike?.bikeCode || bike?.code}</strong>
				<br />
				{t(bike?.gear_type)} &ndash; {bike?.brand} &ndash; {bike?.model} &ndash;{" "}
				{bike?.color} <br />
				{normalizeBoolean(bike?.isVAE)
					? t("type_electric", { ns: "bike" })
					: t("type_mecanical", { ns: "bike" })}
			</p>
			<div className="flex justify-between flex-col md:flex-row-reverse mt-7">
				<button
					data-cy="button-confirm"
					type="button"
					className="c-btn c-btn--primary px-7 mb-5 lg:m-0 uppercase"
					onClick={validateAndSell}
				>
					{t("global.confirm")}
				</button>

				<button
					data-cy="button-cancel"
					type="button"
					className="c-btn c-btn--white-bordered px-7 mb-5 lg:m-0 uppercase"
					onClick={hideModal}
				>
					{t("global.cancel")}
				</button>
			</div>
		</div>
	)
}
